import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { inr, downloadFile } from "@/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FileDown, CheckCircle2, IndianRupee } from "lucide-react";

export default function Payroll() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    api.get("/payroll", { params: { month } }).then((r) => setData(r.data)).catch(() => toast.error("Failed to load payroll"));
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const markPaid = async (row) => {
    if (!window.confirm(`Mark ${row.name}'s salary ${inr(row.net)} for ${month} as PAID? This adds it to expenses.`)) return;
    try {
      await api.post("/payroll/mark-paid", { user_id: row.user_id, month, amount: row.net });
      toast.success(`${row.name} marked paid — expense recorded`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const totals = data ? data.rows.reduce((a, r) => ({ salary: a.salary + r.salary, net: a.net + r.net, ded: a.ded + r.deduction }), { salary: 0, net: 0, ded: 0 }) : null;

  return (
    <div className="space-y-5" data-testid="payroll-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Payroll</h1>
          <p className="text-sm text-muted-foreground">Salary = monthly − absence deductions + overtime · payslips & mark-paid</p>
        </div>
        <Input type="month" data-testid="payroll-month-input" value={month} onChange={(e) => setMonth(e.target.value)} className="w-40 h-9" />
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-4">
          <Card className="p-4 rounded-2xl" data-testid="payroll-total-salary"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total Gross</div><div className="font-mono text-2xl font-bold mt-1">{inr(totals.salary)}</div></Card>
          <Card className="p-4 rounded-2xl" data-testid="payroll-total-ded"><div className="text-xs uppercase tracking-widest text-muted-foreground">Total Deductions</div><div className="font-mono text-2xl font-bold mt-1 text-danger-foreground">−{inr(totals.ded)}</div></Card>
          <Card className="p-4 rounded-2xl" data-testid="payroll-total-net"><div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1"><IndianRupee size={12} /> Net Payable</div><div className="font-mono text-2xl font-bold mt-1 text-success">{inr(totals.net)}</div></Card>
        </div>
      )}

      <Card className="overflow-x-auto rounded-2xl">
        <table className="w-full text-sm min-w-[900px]" data-testid="payroll-table">
          <thead className="bg-muted/60">
            <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground">
              <th className="px-4 py-3">Staff</th><th className="px-4 py-3 text-right">Salary</th>
              <th className="px-4 py-3 text-right">Present</th><th className="px-4 py-3 text-right">Absent</th>
              <th className="px-4 py-3 text-right">OT</th><th className="px-4 py-3 text-right">Deductions</th>
              <th className="px-4 py-3 text-right">Net Pay</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.user_id} className="border-t" data-testid={`payroll-row-${r.user_id}`}>
                <td className="px-4 py-3"><div className="font-medium">{r.name}</div><div className="text-xs text-muted-foreground">{r.department || r.role}</div></td>
                <td className="px-4 py-3 text-right font-mono">{inr(r.salary)}</td>
                <td className="px-4 py-3 text-right font-mono text-success">{r.present}</td>
                <td className="px-4 py-3 text-right font-mono text-danger-foreground">{r.absent}</td>
                <td className="px-4 py-3 text-right font-mono">{r.overtime_days}</td>
                <td className="px-4 py-3 text-right font-mono text-danger-foreground">−{inr(r.deduction)}</td>
                <td className="px-4 py-3 text-right font-mono font-bold">{inr(r.net)}</td>
                <td className="px-4 py-3">{r.paid
                  ? <span className="text-xs px-2 py-1 rounded-full border bg-success-tint text-success border-success/30">Paid {r.paid_at?.slice(0, 10)}</span>
                  : <span className="text-xs px-2 py-1 rounded-full border bg-warning-tint text-warning border-warning/30">Unpaid</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1.5 justify-end">
                    <Button size="sm" variant="outline" className="h-8" data-testid={`payslip-${r.user_id}`} onClick={() => downloadFile(`/payroll/payslip.pdf?user_id=${r.user_id}&month=${month}`, `payslip-${month}.pdf`).then(() => toast.success("Payslip downloaded")).catch(() => toast.error("Failed"))} title="Payslip PDF"><FileDown size={13} /></Button>
                    {!r.paid && <Button size="sm" variant="outline" className="h-8 text-success border-success/40" data-testid={`mark-paid-${r.user_id}`} onClick={() => markPaid(r)}><CheckCircle2 size={13} className="mr-1" /> Pay</Button>}
                  </div>
                </td>
              </tr>
            ))}
            {data && !data.rows.length && <tr><td colSpan={9} className="px-4 py-10 text-center text-muted-foreground">No active staff with salaries</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
